import React, {
	useCallback,
	useEffect,
	useRef,
} from 'react';
import Picker from '@emoji-mart/react';
import PropTypes from 'prop-types';
import * as reactDeviceDetectModule from 'react-device-detect';

const { isMobile } = reactDeviceDetectModule.default || reactDeviceDetectModule;
import ReactDOM from 'react-dom';
import {
	Modal,
	ModalBody,
	Popover,
	PopoverBody
} from 'reactstrap';

import { EMOJI_DATA, EMOJI_SET } from '../../utils/emoji';
import IconButton from '../IconButton';

import usePickerState from './usePickerState';

import './EmojiPicker.scss';
import { ignoreInFlightEvent } from '../../utils/ignoreInFlightEvent';

const EMOJI_PICKER_WRAPPER_POPOVER = 'popover';
const EMOJI_PICKER_WRAPPER_MODAL = 'modal';

const propTypes = {

	/**
	 * Flag indicating if the picker state is controlled by the parent
	 * or within the component.
	 */
	uncontrolled: PropTypes.bool,

	/**
	 * The UI element to wrap the emoji picker.
	 */
	wrapper: PropTypes.oneOf([
		EMOJI_PICKER_WRAPPER_POPOVER,
		EMOJI_PICKER_WRAPPER_MODAL,
	]),

	/**
	 * Tab index for the emoji picker toggle button, if any.
	 */
	tabIndex: PropTypes.string,

	/**
	 * The ID for the trigger button.
	 * Required if the "uncontrolled" prop is false
	 * and using the "popover" wrapper.
	 */
	triggerButtonId: PropTypes.string,

	/**
	 * The custom CSS for the trigger button (if any).
	 */
	triggerButtonClassName: PropTypes.string,

	/**
	 * The HTML size for the trigger button (in px).
	 */
	triggerButtonSize: PropTypes.string,

	/**
	 * The HTML title for the trigger button.
	 */
	triggerTitle: PropTypes.string,

	/**
	 * The HTML aria-label for the trigger button.
	 */
	triggerAriaLabel: PropTypes.string,

	/**
	 * The placement of the popover.
	 */
	popoverPlacement: PropTypes.string,

	/**
	 * The modifiers (if any) of the popover.
	 */
	popoverModifiers: PropTypes.arrayOf(PropTypes.shape({

		// The modifier name
		name: PropTypes.string,

	})),

	/**
	 * The custom CSS class name for the modal (if any).
	 */
	modalClassName: PropTypes.string,

	/**
	 * Flag indicating if the picker is open or not.
	 * Required if the "uncontrolled" prop is false.
	 */
	isOpen: PropTypes.bool,

	/**
	 * Callback function to toggle the picker visibility state.
	 * Required if the "uncontrolled" prop is false.
	 */
	setIsOpen: PropTypes.func,

	/**
	 * Optional callback function to handle any effects
	 * after clicking the emoji picker toggle.
	 */
	onClick: PropTypes.func,

	/**
	 * Callback function to handle selecting an emoji.
	 */
	onSelect: PropTypes.func.isRequired,

};

const defaultProps = {
	triggerButtonClassName: '',
	triggerButtonSize: '30px',
	modalClassName: '',
	popoverPlacement: 'top-end',
	wrapper: EMOJI_PICKER_WRAPPER_POPOVER,
	uncontrolled: true,
};

/**
 * Renders component for user to select an emoji.
 * The picker can either be wrapped in a modal or popover.
 * If the "uncontrolled" prop is set to true, a button will
 * also be rendered that triggers the visibility of the picker, otherwise
 * the visibility state must be controlled from the parent.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const EmojiPicker = (props) => {

	const {
		tabIndex,
		triggerButtonId,
		triggerButtonClassName,
		triggerButtonSize,
		triggerTitle,
		triggerAriaLabel,
		popoverPlacement,
		popoverModifiers,
		modalClassName,
		wrapper,
		uncontrolled,
		onClick,
		onSelect,
		...restProps
	} = props;

	const emojiMartRef = useRef(null);

	const [showPicker, setShowPicker] = usePickerState(props);

	const uncontrolledPopoverTargetID = isMobile
		? triggerButtonId
		: `tc-icon-btn-popover-trigger-${triggerButtonId}`;

	/**
	 * Toggle picker for uncontrolled instances.
	 */
	const togglePicker = useCallback((value) => {

		if (value === undefined || value === null) {

			setShowPicker((prev) => {
				if (onClick) {
					onClick(!prev);
				}
				return !prev;
			});

		} else {

			setShowPicker(value);
			if (onClick) {
				onClick(value);
			}

		}

	}, [setShowPicker, onClick]);

	/**
	 * Add additional styling to emoji-mart shadow dom elements.
	 */
	useEffect(() => {

		try {

			// Find the root element of the shadow dom for the emoji picker
			const shadowRoot = emojiMartRef.current?.querySelector('em-emoji-picker').shadowRoot;

			if (shadowRoot) {

				// Auto-focus the search input for the emoji picker
				if (!isMobile) {

					// NOTE: Need to use timeout in order for the shadow root
					//       DOM elements to be queryable
					setTimeout(() => {

						shadowRoot.querySelector('input[type="search"]')?.focus({
							preventScroll: true
						});

					}, 100);

				}

				// Create a new CSS style sheet
				const newSheet = new CSSStyleSheet();

				// Add our CSS rules to the style sheet
				newSheet.replace(`
					.scroll {
						height: 200px;
						max-height: 200px;
					}
					.menu[role="radiogroup"] {
						display: flex;
						box-shadow: 1px 1px 5px hsla(0, 0%, 0%, 0.50);
					}
					.menu[role="radiogroup"] button span:last-child {
						display: none;
					}
				`).then((result) => {

					// Add the new CSS style sheet to the shadow dom
					shadowRoot.adoptedStyleSheets = [
						...shadowRoot.adoptedStyleSheets,
						result
					];

				});

			}

		} catch (error) {
			console.error(error);
		}

	}, [showPicker, emojiMartRef]);

	/**
	 * Add additional styilng to emoji-mart shadow dom elements
	 * for the modal wrapper.
	 */
	useEffect(() => {

		if (wrapper !== EMOJI_PICKER_WRAPPER_MODAL) {
			return;
		}

		try {

			// NOTE: Need to use timeout in order for the shadow root
			// DOM elements to be queryable
			setTimeout(() => {

				const shadowRoot = emojiMartRef.current?.querySelector('em-emoji-picker').shadowRoot;

				if (shadowRoot) {

					const newSheet = new CSSStyleSheet();

					newSheet.replace(`
						#root {
							width: 100% !important;
						}
						.scroll {
							padding-right: 12px;
						}
						.scroll > div {
							width: 100% !important;
						}
					`).then((result) => {
						shadowRoot.adoptedStyleSheets = [
							...shadowRoot.adoptedStyleSheets,
							result
						];
					});

				}

			}, 100);

		} catch (error) {

			console.error(error);

		}

	}, [showPicker, wrapper, emojiMartRef]);

	/**
	 * Listen for 'Escape' keydown on window and hide picker if pressed.
	 */
	useEffect(() => {

		// TODO: Fix bug with emoji-mart requiring 2 escape key presses

		const hidePicker = ignoreInFlightEvent((event) => {

			if (showPicker && event.key === 'Escape') {

				// Close the popover
				togglePicker(false);

				// Re-focus on the trigger button
				const triggerButton = document.getElementById(uncontrolledPopoverTargetID);

				if (triggerButton) {
					triggerButton.focus();
				}

			}

		});

		window.addEventListener('keydown', hidePicker);

		return () => {
			window.removeEventListener('keydown', hidePicker);
		};

	}, [uncontrolledPopoverTargetID, showPicker, togglePicker]);

	/**
	 * Toggle uncontrolled picker closed and call select callback.
	 */
	const handleSelectEmoji = useCallback((data) => {

		onSelect(data);
		togglePicker(false);

	}, [onSelect, togglePicker]);

	const picker = (
		<div ref={emojiMartRef}>
			<Picker
				data={EMOJI_DATA}
				set={EMOJI_SET}
				onEmojiSelect={handleSelectEmoji}
				onClickOutside={() => togglePicker(false)} />
		</div>
	);

	return (
		<div
			className="tc-emoji-picker-container"
			{...restProps}>
			{uncontrolled &&
				<IconButton
					id={triggerButtonId}
					className={`tc-emoji-picker-trigger ${triggerButtonClassName}`}
					icon="far fa-smile-beam fa-lg"
					size={triggerButtonSize}
					title={triggerTitle}
					aria-expanded={showPicker}
					aria-label={triggerAriaLabel || triggerTitle}
					tabIndex={tabIndex}
					tooltip={{
						enable: !isMobile,
						placement: 'top',
						container: 'body',
					}}
					onClick={
						(wrapper === EMOJI_PICKER_WRAPPER_MODAL && uncontrolled)
							? togglePicker
							: undefined
					} />
			}
			{wrapper === EMOJI_PICKER_WRAPPER_POPOVER &&
				<Popover
					className="tc-emoji-picker-popover"
					target={uncontrolled ? uncontrolledPopoverTargetID : triggerButtonId}
					placement={popoverPlacement}
					modifiers={popoverModifiers}
					fade={false}
					isOpen={showPicker}
					trigger="click"
					toggle={togglePicker}>
					<PopoverBody className="p-0">
						{picker}
					</PopoverBody>
				</Popover>
			}
			{wrapper === EMOJI_PICKER_WRAPPER_MODAL &&
				<Modal
					className={`tc-emoji-picker-modal ${modalClassName || ''}`}
					isOpen={showPicker}
					toggle={togglePicker}
					scrollable>
					<ModalBody>
						{picker}
					</ModalBody>
				</Modal>
			}
		</div>
	);

};

EmojiPicker.propTypes = propTypes;
EmojiPicker.defaultProps = defaultProps;

export default EmojiPicker;
